import type { RemunerationProfileInsertPayload } from "@asap/shared/schemas/remuneration/remuneration-profile";
import type { RemunerationTemplate } from "@asap/shared/schemas/remuneration/remuneration-template";
import { formatRemunerationDecimal, resolveThirteenthMonthAmount } from "@asap/shared/utils/remuneration";
import { SMIC_MINIMUM } from "@asap/shared/utils/remuneration.constants";
import type { RemunerationProfileOrigin } from "@asap/shared/schemas/remuneration/remuneration-profile-data";
import { isStructuredRemunerationProfileOrigin } from "@asap/shared/schemas/remuneration/remuneration-profile-data";
import { getDefaultRemunerationProfileInsertPayload } from "@asap/shared/utils/remuneration-profile-storage";
import { formatStructuredOvertimeLabel } from "@asap/shared/utils/remuneration-overtime-label";
import {
  postModificationAmendmentBodySchema,
  type PostModificationAmendmentBody,
} from "@asap/shared/schemas/contract/modification-amendment";
import {
  postExtensionAmendmentBodySchema,
  type PostExtensionAmendmentBody,
} from "@asap/shared/schemas/contract/extension-amendment";
import type { EmployeeStatus } from "@asap/shared/types/supabase.types.gen";

export type RemunerationTemplateSelectMenuItem = {
  id: string;
  name: string;
  primeCount: number;
  hourlyRateLabel: string;
  hoursCoefficientLabel: string;
  subtitle: string;
  searchLabel: string;
};

const formatPrimeCountLabel = (primeCount: number): string =>
  primeCount <= 1 ? `${primeCount} prime` : `${primeCount} primes`;

const formatHourlyRateLabel = (hourlyRate: number): string => `${formatRemunerationDecimal(hourlyRate)}€/h`;

const formatHoursCoefficientLabel = (hoursCoefficient: number): string => formatRemunerationDecimal(hoursCoefficient);

export const formatRemunerationTemplateSelectMenuSubtitle = (template: RemunerationTemplate): string => {
  const primeCount = template.profile.indemnities.length;
  return [
    formatPrimeCountLabel(primeCount),
    `Taux horaire : ${formatHourlyRateLabel(template.profile.hourly_rate)}`,
    `Coef : ${formatHoursCoefficientLabel(template.profile.hours_coefficient)}`,
  ].join(" · ");
};

export const buildRemunerationTemplateSelectMenuItem = (
  template: RemunerationTemplate
): RemunerationTemplateSelectMenuItem => {
  const subtitle = formatRemunerationTemplateSelectMenuSubtitle(template);
  return {
    id: template.id,
    name: template.name,
    primeCount: template.profile.indemnities.length,
    hourlyRateLabel: formatHourlyRateLabel(template.profile.hourly_rate),
    hoursCoefficientLabel: formatHoursCoefficientLabel(template.profile.hours_coefficient),
    subtitle,
    searchLabel: `${template.name} ${subtitle}`,
  };
};

export const createEmptyRemunerationProfileInsertPayload = getDefaultRemunerationProfileInsertPayload;

export const getRemunerationHourlyRateErrorMessage = (hourlyRate: unknown): string | undefined => {
  if (typeof hourlyRate === "number" && hourlyRate >= SMIC_MINIMUM) {
    return undefined;
  }
  return `Le taux horaire doit être au moins égal au SMIC (${SMIC_MINIMUM} €)`;
};

type RemunerationProfileFieldErrorInput = {
  hourly_rate?: unknown;
  rtt_enabled?: boolean;
  rtt_weekly_hours?: number | null;
};

export const hasRemunerationRttWeeklyHoursError = (
  profile: Pick<RemunerationProfileFieldErrorInput, "rtt_enabled" | "rtt_weekly_hours"> | undefined
): boolean => {
  if (profile?.rtt_enabled !== true) return false;
  return typeof profile.rtt_weekly_hours !== "number" || profile.rtt_weekly_hours <= 0;
};

export const getRemunerationProfileFieldErrorMessage = (
  profile: RemunerationProfileFieldErrorInput | undefined,
  rttWeeklyHoursMessage: string
): string | undefined => {
  const hourlyRateErrorMessage = getRemunerationHourlyRateErrorMessage(profile?.hourly_rate);
  if (hourlyRateErrorMessage) return hourlyRateErrorMessage;
  if (hasRemunerationRttWeeklyHoursError(profile)) return rttWeeklyHoursMessage;
  return undefined;
};

/** Maps API template profile (view indemnities with ids) to insert-shaped profile for contract forms. */
export type ContractCreatePayloadWithNestedRemunerationProfile<T extends Record<string, unknown>> = T & {
  remuneration_profile?: RemunerationProfileInsertPayload;
};

export const buildRemunerationProfileSnapshotForContractCreateSave = (
  profile: RemunerationProfileInsertPayload
): RemunerationProfileInsertPayload => ({
  ...profile,
});

export const attachNestedRemunerationProfileToContractCreatePayload = <T extends Record<string, unknown>>(
  contractPayload: T,
  options: {
    shouldAttachStructuredRemunerationProfile: boolean;
    remunerationProfile: RemunerationProfileInsertPayload;
  }
): ContractCreatePayloadWithNestedRemunerationProfile<T> => {
  if (!options.shouldAttachStructuredRemunerationProfile) {
    return contractPayload;
  }

  return {
    ...contractPayload,
    remuneration_profile: buildRemunerationProfileSnapshotForContractCreateSave(options.remunerationProfile),
  };
};

/** Attaches nested `remuneration_profile` for POST /contracts/modification-amendments when structured remu is active. */
export const attachNestedRemunerationProfileToModificationAmendmentPayload = <T extends Record<string, unknown>>(
  amendmentPayload: T,
  options: {
    shouldAttachStructuredRemunerationProfile: boolean;
    remunerationProfile: RemunerationProfileInsertPayload;
  }
): ContractCreatePayloadWithNestedRemunerationProfile<T> =>
  attachNestedRemunerationProfileToContractCreatePayload(amendmentPayload, options);

/** Builds POST /contracts/modification-amendments payloads for all modification saves (FF on/off). */
export const buildModificationAmendmentApiPayloadFromValidatedInsert = (
  validatedAmendmentInsert: Record<string, unknown>,
  options: {
    shouldAttachStructuredRemunerationProfile: boolean;
    remunerationProfile: RemunerationProfileInsertPayload;
  }
): PostModificationAmendmentBody =>
  postModificationAmendmentBodySchema.parse(
    attachNestedRemunerationProfileToModificationAmendmentPayload(validatedAmendmentInsert, options)
  );

/** Builds POST /contracts/extension-amendments payloads for all extension saves. */
export const buildExtensionAmendmentApiPayloadFromValidatedInsert = (
  validatedAmendmentInsert: Record<string, unknown>,
  parentRemunerationProfileId?: string | null
): PostExtensionAmendmentBody =>
  postExtensionAmendmentBodySchema.parse({
    ...validatedAmendmentInsert,
    ...(parentRemunerationProfileId ? { parent_remuneration_profile_id: parentRemunerationProfileId } : {}),
  });

const isStructuredRemunerationBlockEligible = (options: {
  isRemunerationElementsContractEnabled: boolean;
  remunerationProfileId: string | null | undefined;
  remunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean =>
  options.isRemunerationElementsContractEnabled &&
  Boolean(options.remunerationProfileId) &&
  isStructuredRemunerationProfileOrigin(options.remunerationProfileOrigin);

/** FF ON + profile FK but origin not yet resolved (in-flight GET or failed GET). */
export const isContractRemunerationProfileLoadUnresolved = (options: {
  isRemunerationElementsContractEnabled: boolean;
  remunerationProfileId: string | null | undefined;
  remunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean =>
  options.isRemunerationElementsContractEnabled &&
  Boolean(options.remunerationProfileId) &&
  options.remunerationProfileOrigin == null;

export const CONTRACT_REMUNERATION_PROFILE_LOAD_UNRESOLVED_MESSAGE =
  "Impossible de charger le profil de rémunération. Enregistrement désactivé — veuillez rafraîchir la page.";

/** FF ON + source contract with structured profile → renewal form. */
export const shouldShowStructuredRemunerationBlockForContractRenewal = (options: {
  isRemunerationElementsContractEnabled: boolean;
  sourceRemunerationProfileId: string | null | undefined;
  sourceRemunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean =>
  isStructuredRemunerationBlockEligible({
    isRemunerationElementsContractEnabled: options.isRemunerationElementsContractEnabled,
    remunerationProfileId: options.sourceRemunerationProfileId,
    remunerationProfileOrigin: options.sourceRemunerationProfileOrigin,
  });

export const prepareContractRenewalPayloadForApiCreate = <T extends Record<string, unknown>>(
  renewalPayload: T,
  options: {
    shouldAttachStructuredRemunerationProfile: boolean;
    remunerationProfile: RemunerationProfileInsertPayload;
  }
): ContractCreatePayloadWithNestedRemunerationProfile<T> =>
  attachNestedRemunerationProfileToContractCreatePayload(renewalPayload, options);

/** FF ON + existing contract with structured profile → PATCH before Supabase update. */
export const shouldPersistStructuredRemunerationProfileOnExistingContract = (options: {
  shouldShowStructuredRemunerationBlock: boolean;
  hasExistingContract: boolean;
}): boolean => options.shouldShowStructuredRemunerationBlock && options.hasExistingContract;

/** FF ON + contract/amendment row with structured profile → read-only detail. */
export const shouldShowStructuredRemunerationBlockForContractRead = (options: {
  isRemunerationElementsContractEnabled: boolean;
  remunerationProfileId: string | null | undefined;
  remunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean => isStructuredRemunerationBlockEligible(options);

/**
 * Contract form: structured block on create-from-scratch and on duplicate when the flag is on.
 * Editing an existing contract keeps it only when that contract already has a structured remu profile;
 * otherwise the legacy primes fields stay visible (no profile, or backfilled legacy_import).
 */
export const shouldShowStructuredRemunerationBlockForContractForm = (options: {
  isRemunerationElementsEnabled: boolean;
  existingContract: { remuneration_profile_id?: string | null } | null | undefined;
  remunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean => {
  if (!options.isRemunerationElementsEnabled) {
    return false;
  }
  if (options.existingContract) {
    return shouldShowStructuredRemunerationBlockForContractRead({
      isRemunerationElementsContractEnabled: true,
      remunerationProfileId: options.existingContract.remuneration_profile_id,
      remunerationProfileOrigin: options.remunerationProfileOrigin,
    });
  }
  return true;
};

/** Free-text legacy primes of a duplicated contract that were not converted into the structured profile. */
export const getLegacyPrimesLeftToCarryOver = (options: {
  duplicateContract:
    | {
        compensation_bonus_with_charges?: string | null;
        compensation_bonus_without_charges?: string | null;
      }
    | null
    | undefined;
  duplicateRemunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): { taxablePrimes: string; nonTaxablePrimes: string } | undefined => {
  if (!options.duplicateContract || isStructuredRemunerationProfileOrigin(options.duplicateRemunerationProfileOrigin)) {
    return undefined;
  }
  const taxablePrimes = options.duplicateContract.compensation_bonus_with_charges?.trim() ?? "";
  const nonTaxablePrimes = options.duplicateContract.compensation_bonus_without_charges?.trim() ?? "";
  if (!taxablePrimes && !nonTaxablePrimes) {
    return undefined;
  }
  return { taxablePrimes, nonTaxablePrimes };
};

/** Amendment form: structured block when reading the saved row, or editing a modificatif on a structured parent. */
export const shouldShowStructuredRemunerationBlockForAmendmentForm = (options: {
  isRemunerationElementsContractEnabled: boolean;
  isReading: boolean;
  isExistingAmendment?: boolean;
  amendmentType: "extension" | "modification" | undefined;
  remunerationProfileId: string | null | undefined;
  remunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean => {
  if (options.isReading || options.isExistingAmendment) {
    return shouldShowStructuredRemunerationBlockForContractRead({
      isRemunerationElementsContractEnabled: options.isRemunerationElementsContractEnabled,
      remunerationProfileId: options.remunerationProfileId,
      remunerationProfileOrigin: options.remunerationProfileOrigin,
    });
  }

  return shouldShowStructuredRemunerationBlockForModificationAmendment({
    isRemunerationElementsContractEnabled: options.isRemunerationElementsContractEnabled,
    amendmentType: options.amendmentType,
    isReading: false,
    parentRemunerationProfileId: options.remunerationProfileId,
    parentRemunerationProfileOrigin: options.remunerationProfileOrigin,
  });
};

/** FF ON + modificatif + parent with structured profile. Extension and legacy parents keep the old fields. */
export const shouldShowStructuredRemunerationBlockForModificationAmendment = (options: {
  isRemunerationElementsContractEnabled: boolean;
  amendmentType: "extension" | "modification" | undefined;
  isReading: boolean;
  parentRemunerationProfileId: string | null | undefined;
  parentRemunerationProfileOrigin: RemunerationProfileOrigin | null | undefined;
}): boolean => {
  if (options.isReading) {
    return false;
  }
  if (options.amendmentType !== "modification") {
    return false;
  }
  return isStructuredRemunerationBlockEligible({
    isRemunerationElementsContractEnabled: options.isRemunerationElementsContractEnabled,
    remunerationProfileId: options.parentRemunerationProfileId,
    remunerationProfileOrigin: options.parentRemunerationProfileOrigin,
  });
};

export const syncRemunerationProfileToAmendmentLegacyScalars = (
  profile: RemunerationProfileInsertPayload,
  overtimeWeeklyHours?: number | null
): {
  hourlyWage: number;
  coefficient: number;
  overtime: string;
  employeeStatus: EmployeeStatus | null;
} => ({
  hourlyWage: profile.hourly_rate,
  coefficient: profile.hours_coefficient,
  overtime: formatStructuredOvertimeLabel({
    ...profile,
    overtime_weekly_hours: overtimeWeeklyHours,
  }),
  employeeStatus: profile.employee_status,
});

/** Contract select loads `agency.company_id`; mission join only exposes company name. */
export const resolveRemunerationBlockCompanyIdFromContract = (
  contract: { agency?: { company_id?: string | null } | null } | null | undefined
): string | undefined => contract?.agency?.company_id ?? undefined;

export const resolveRemunerationBlockOrganizationIdFromContract = (
  contract: { organization_id?: string | null } | null | undefined,
  consultantDefaultOrganizationId?: string | null
): string | undefined => contract?.organization_id ?? consultantDefaultOrganizationId ?? undefined;

export const mapRemunerationTemplateToProfileInsertPayload = (
  template: RemunerationTemplate
): RemunerationProfileInsertPayload => ({
  hours_coefficient: template.profile.hours_coefficient,
  bonus_coefficient: template.profile.bonus_coefficient,
  employee_status: template.profile.employee_status,
  hourly_rate: template.profile.hourly_rate,
  thirteenth_month_enabled: template.profile.thirteenth_month_enabled,
  thirteenth_month_unit: template.profile.thirteenth_month_unit ?? "hour",
  thirteenth_month_amount: resolveThirteenthMonthAmount({
    hourlyRate: template.profile.hourly_rate,
    thirteenthMonthEnabled: template.profile.thirteenth_month_enabled,
    thirteenthMonthUnit: template.profile.thirteenth_month_unit,
    thirteenthMonthAmount: template.profile.thirteenth_month_amount,
  }),
  rtt_enabled: template.profile.rtt_enabled,
  rtt_weekly_hours: template.profile.rtt_weekly_hours ?? undefined,
  overtime_enabled: template.profile.overtime_enabled,
  overtime_has_custom_convention: template.profile.overtime_enabled
    ? (template.profile.overtime_has_custom_convention ?? false)
    : undefined,
  overtime_rate_36_to_43_percent: template.profile.overtime_rate_36_to_43_percent ?? undefined,
  overtime_rate_above_43_percent: template.profile.overtime_rate_above_43_percent ?? undefined,
  indemnities: template.profile.indemnities.map((indemnity) => ({
    pld_prime_id: indemnity.pld_prime_id,
    pld_prime_id_taxable: indemnity.pld_prime_id_taxable,
    name: indemnity.name,
    complement_label: indemnity.complement_label,
    amount: indemnity.amount,
    amount_non_taxable: indemnity.amount_non_taxable,
    coefficient: indemnity.coefficient,
    taxable_coefficient: indemnity.taxable_coefficient,
  })),
});
